import React from 'react'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useController, UseControllerProps } from 'react-hook-form'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

interface Currency {
  code: string
  displayName: string
}

interface Props {
  label?: string
  placeholder?: string
  currenciesData: Currency[]
}

export function InputCurrencyForm(props: Props & UseControllerProps) {
  const { control, name, label, placeholder, currenciesData } = props
  const { field, fieldState } = useController(props)

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    let amount = 0

    if (value && value.trim() !== '') {
      const parsedValue = parseFloat(value)
      amount = isNaN(parsedValue) ? 0 : parsedValue
    }

    field.onChange({
      amount: amount,
      codeCurrency: field.value?.codeCurrency || 'USD',
    })
  }

  const handleCurrencyChange = (codeCurrency: string) => {
    // Get the current amount value, handling nested structure if it exists
    let currentAmount = 0
    if (field.value?.amount !== undefined) {
      if (typeof field.value.amount === 'number') {
        currentAmount = field.value.amount
      } else if (
        typeof field.value.amount === 'object' &&
        field.value.amount?.amount !== undefined
      ) {
        // Handle nested structure case
        currentAmount = field.value.amount.amount
      }
    }

    field.onChange({
      amount: currentAmount,
      codeCurrency: codeCurrency,
    })
  }

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    // 1. Aserta el tipo de event.target a HTMLInputElement
    const inputElement = event.target as HTMLInputElement

    const allowedKeys = [
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '.',
      '-',
      'Tab',
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
    ]

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault()
    }

    // Ahora `inputElement.value` está correctamente tipado
    if (event.key === '.' && inputElement.value.includes('.')) {
      event.preventDefault()
    }

    if (event.key === '-') {
      if (
        inputElement.value.includes('-') ||
        inputElement.selectionStart !== 0
      ) {
        event.preventDefault()
      }
    }
  }

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <FormControl>
            <div className="flex w-full gap-1">
              <Input
                className={'w-2/3'}
                min="0"
                onChange={handleAmountChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                step="0.01"
                type="number"
                value={
                  field.value?.amount !== undefined &&
                  field.value?.amount !== null
                    ? field.value.amount
                    : ''
                }
              />
              <div className={'w-1/3'}>
                <Select
                  onValueChange={handleCurrencyChange}
                  value={field.value?.codeCurrency || ''}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {currenciesData.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </FormControl>
          <FormMessage>{fieldState.error?.message}</FormMessage>
        </FormItem>
      )}
    />
  )
}
